import React, { useState, useMemo, useRef, useEffect } from 'react';
import '../styles/Update.css';

/* ---------- Scroll-reveal wrapper (no external libs) ---------- */
const Reveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </Tag>
  );
};

/* ---------- Inline icons ---------- */
const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconBook = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function Update() {
  return (
    <div className="app">
      {/* ============ HEADER ============ */}
      <header className="header">
        <div className="header-glow header-glow-1"></div>
        <div className="header-glow header-glow-2"></div>
        <div className="header-grain"></div>

        <div className="header-inner">
          <span className="header-badge">
            <IconBook />
            22ஆம் ஆண்டு &nbsp;•&nbsp; 2026
          </span>
          <h1>ஈரோடு புத்தகத் திருவிழா</h1>
          <h2>Erode Book Festival</h2>
          <p className="subtitle">
            தமிழ்நாடு அரசு மற்றும் மக்கள் சிந்தனைப் பேரவை இணைந்து நடத்தும்
            22 ஆம் ஆண்டு ஈரோடு புத்தகத் திருவிழா
          </p>
          <span className="date-chip">
            <IconCalendar />
            அரங்க ஒதுக்கீடு குலுக்கல் நிகழ்வு: 21.07.2026
          </span>
        </div>
      </header>

      <div className="content">
        <Reveal as="div" className="announcement">
          <span className="announcement-icon">
            <IconBook />
          </span>
          <p>
            ஈரோடு புத்தகத் திருவிழாவில் அரங்குகள் அமைக்கும் நிறுவனங்கள் மற்றும்
            குலுக்கலின் மூலம் தேர்வு செய்யப்பட்ட அரங்க எண்கள் அடங்கிய பட்டியல்
          </p>
        </Reveal>

        <div className="stall-lists">
          <StallList
            title="Single Stall List"
            subtitle={`${singleStallData.length} நிறுவனங்கள்`}
            data={singleStallData}
          />
          <StallList
            title="Double Stall List"
            subtitle={`${doubleStallData.length} நிறுவனங்கள்`}
            data={doubleStallData}
            isDouble
          />
        </div>
      </div>
    </div>
  );
}

const StallList = ({ title, subtitle, data, isDouble = false }) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.stallNumber.toLowerCase().includes(q)
    );
  }, [data, query]);

  return (
    <Reveal as="div" className={`stall-list ${isDouble ? 'double' : ''}`}>
      <div className="stall-list-head">
        <div>
          <h3>{title}</h3>
          <span className="stall-list-count">{subtitle}</span>
        </div>
        <div className="search-box">
          <IconSearch />
          <input
            type="text"
            placeholder="தேடு... (பெயர் / எண்)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <IconClose />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">பொருந்தும் முடிவுகள் இல்லை</div>
      ) : (
        <ul className="stall-entries">
          {filtered.map((item, index) => (
            <li className="stall-entry" key={`${item.sno}-${item.name}`}>
              <span className="entry-sno">{item.sno}</span>
              <span className="entry-name">{item.name}</span>
              <span className="entry-stall">
                {isDouble
                  ? item.stallNumber.split(',').map((n, i) => (
                      <span className="stall-chip" key={i}>
                        {n.trim()}
                      </span>
                    ))
                  : <span className="stall-chip">{item.stallNumber}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Reveal>
  );
};

/* ============ Single stall data (2026) ============ */
const singleStallData = [
  { sno: 1, name: 'AAMU PUBLICATION', stallNumber: '212' },
  { sno: 2, name: 'AGASTHIAR PUBLICATIONS', stallNumber: '86' },
  { sno: 3, name: 'ANANDHA NILAYAM', stallNumber: '37' },
  { sno: 4, name: 'ANDHIMAZHAI', stallNumber: '143' },
  { sno: 5, name: 'ANNAM PUBLISHERS', stallNumber: '38' },
  { sno: 6, name: 'ARUL PATHIPPAGAM', stallNumber: '123' },
  { sno: 7, name: 'ARUN PATHIPPAGAM', stallNumber: '12' },
  { sno: 8, name: 'B. RATHNA NAYAKAR & SONS', stallNumber: '4' },
  { sno: 9, name: 'ERODE PADAIPPALARGAL', stallNumber: '11' },
  { sno: 10, name: 'EUREKA BOOKS', stallNumber: '150' },
  { sno: 11, name: 'EXPRESS PUBLICATIONS ( DINAMANI )', stallNumber: '192' },
  { sno: 12, name: 'GANGAARAANI PATHIPPAGAM', stallNumber: '24' },
  { sno: 13, name: 'GEETHAM PUBLICATIONS', stallNumber: '215' },
  { sno: 14, name: 'GIRI LAW HOUSE', stallNumber: '201' },
  { sno: 15, name: 'GIRI TRADING AGENCY PVT.LTD', stallNumber: '96' },
  { sno: 16, name: 'GOWRA PUTHAKA MAIYAM', stallNumber: '139' },
  { sno: 17, name: 'HARISH BOOKS', stallNumber: '211' },
  { sno: 18, name: 'THE HINDU', stallNumber: '135' },
  { sno: 19, name: 'INTERNATIONAL INSTITUTE OF TAMIL STUDIES', stallNumber: '20' },
  { sno: 20, name: 'ISLAMIC FOUNDATION TRUST', stallNumber: '151' },
  { sno: 21, name: 'IYALVAAGAI PUBLICATION', stallNumber: '51' },
  { sno: 22, name: 'JEEVA PATHIPPAGAM', stallNumber: '126' },
  { sno: 23, name: 'KALAPPAI PATHIPPAGAM', stallNumber: '42' },
  { sno: 24, name: 'KRISHNAMURTHI FOUNDATION INDIA', stallNumber: '49' },
  { sno: 25, name: 'KUMARAN PATHIPPAGAM', stallNumber: '107' },
  { sno: 26, name: 'KUMIZHMUNAI PATHIPPAGAM', stallNumber: '196' },
  { sno: 27, name: 'KUTTI AGAYAM CHILDREN PUBLICATION', stallNumber: '122' },
  { sno: 28, name: 'LKM PUBLICATION', stallNumber: '45' },
  { sno: 29, name: 'MASK', stallNumber: '10' },
  { sno: 30, name: 'MI VELIYEEDU', stallNumber: '77' },
  { sno: 31, name: 'MINNANGADI PUBLICATIONS', stallNumber: '21' },
  { sno: 32, name: 'MUNNETRA PATHIPAGAM', stallNumber: '72' },
  { sno: 33, name: 'MUTHU COMICS', stallNumber: '15' },
  { sno: 34, name: 'NAM PUBLICATION', stallNumber: '74' },
  { sno: 35, name: 'NAN NOOL VELIYITAGAM', stallNumber: '142' },
  { sno: 36, name: 'NANMOZHI PATHIPAGAM', stallNumber: '31' },
  { sno: 37, name: 'NAVINA VIRUTCHAM', stallNumber: '94' },
  { sno: 38, name: 'NIVETHITHA PATHIPPAGAM', stallNumber: '152' },
  { sno: 39, name: 'PALANIAPPA BROTHERS', stallNumber: '149' },
  { sno: 40, name: 'PANMAI VELI', stallNumber: '14' },
  { sno: 41, name: 'PUDHUPUNAL', stallNumber: '56' },
  { sno: 42, name: 'PUSTAKA DIGITAL MEDIA PVT. LTD', stallNumber: '172' },
  { sno: 43, name: 'PUTHAGAKADAI.COM', stallNumber: '91' },
  { sno: 44, name: 'RAJATHI PATHIPAGAM', stallNumber: '136' },
  { sno: 45, name: 'RAJMOHAN PATHIPPAGAM', stallNumber: '110' },
  { sno: 46, name: 'SAALIVAGANAN PATHIPPGAM', stallNumber: '155' },
  { sno: 47, name: 'SATHYAA ENTERPRISES', stallNumber: '206' },
  { sno: 48, name: 'SEER VAASAGAR VATTAM', stallNumber: '73' },
  { sno: 49, name: 'SHANTI BOOKS', stallNumber: '32' },
  { sno: 50, name: 'SIVAGURU PATHIPPAGAM', stallNumber: '125' },
  { sno: 51, name: 'SMILE PUBLISHING INDIA PVT.LTD', stallNumber: '190' },
  { sno: 52, name: 'SREE INDHU PUBLICATIONS', stallNumber: '41' },
  { sno: 53, name: 'SRI PUTHAKA KALANJIYAM', stallNumber: '25' },
  { sno: 54, name: 'SRI SENBAGA PATHIPPAGAM', stallNumber: '78' },
  { sno: 55, name: 'SRI VIVEKANANDA SEVALAYAM', stallNumber: '50' },
  { sno: 56, name: 'SWASAM', stallNumber: '3' },
  { sno: 57, name: 'TAMIL UNIVERSITY', stallNumber: '95' },
  { sno: 58, name: 'TAMIZHINI', stallNumber: '195' },
  { sno: 59, name: 'THAMIZH CHOLAI PATHIPPAGAM', stallNumber: '13' },
  { sno: 60, name: 'TICK SOFT', stallNumber: '175' },
  { sno: 61, name: 'UNIVERSAL PUBLISHING', stallNumber: '85' },
  { sno: 62, name: 'UYIRMMAI PATHIPPAGAM', stallNumber: '46' },
  { sno: 63, name: 'VAGAM PUBLICATION', stallNumber: '71' },
  { sno: 64, name: 'VAIGARAI PATHIPPAGAM', stallNumber: '111' },
  { sno: 65, name: 'VALLI PUTHAGA ULAGAM', stallNumber: '90' },
  { sno: 66, name: 'VAMSI BOOKS', stallNumber: '124' },
  { sno: 67, name: 'VANATHI PATHIPPAKAM', stallNumber: '191' },
  { sno: 68, name: 'VANAVIL PUTHAKALAYAM', stallNumber: '222' },
  { sno: 69, name: 'VASAGASALAI PUBLICATIONS PVT LTD', stallNumber: '28' },
  { sno: 70, name: 'VETHATHIRI PUBLICATIONS', stallNumber: '114' },
  { sno: 71, name: 'VIDIYAL PATHIPPAGAM', stallNumber: '144' },
  { sno: 72, name: 'YAAVARUM PUBLISHERS', stallNumber: '89' }
];

/* ============ Double stall data (2026) ============ */
const doubleStallData = [
  { sno: 1, name: 'ANA BOOKS', stallNumber: '83, 84' },
  { sno: 2, name: 'ANANDA VIKATAN PUBLISHERS PVT LTD', stallNumber: '101, 102' },
  { sno: 3, name: 'APPLE PUBLISHING INTERNATIONAL (P) LTD', stallNumber: '140, 141' },
  { sno: 4, name: 'ARIVU PATHIPPAGAM', stallNumber: '105, 106' },
  { sno: 5, name: 'AROCKIYAA BOOKS', stallNumber: '182, 183' },
  { sno: 6, name: 'ARUNA PUBLICATIONS', stallNumber: '131, 132' },
  { sno: 7, name: 'ARUTCHELVAR Dr. N. MAHALINGAM TRANSLATION INSTITUTE', stallNumber: '207, 208' },
  { sno: 8, name: 'ASIAN BOOK CENTER', stallNumber: '166, 167' },
  { sno: 9, name: 'BASKAR BOOK HOUSE', stallNumber: '176, 177' },
  { sno: 10, name: 'BELL CO', stallNumber: '35, 36' },
  { sno: 11, name: 'BHARATH PUBLISHERS', stallNumber: '126, 187' },
  { sno: 12, name: 'BHARATHI PUTHAKALAYAM', stallNumber: '204, 205' },
  { sno: 13, name: 'BOOK BASKET', stallNumber: '57, 58' },
  { sno: 14, name: 'BOOKS FOR CHILDREN', stallNumber: '213, 214' },
  { sno: 15, name: 'CLASSIC PUBLICATIONS', stallNumber: '168, 169' },
  { sno: 16, name: 'CORAL PUBLISHERS & DISTRIBUTORS', stallNumber: '115, 116' },
  { sno: 17, name: 'DISCOVERY BOOK PALACE ( P ) LTD', stallNumber: '133, 134' },
  { sno: 18, name: 'DREAM WAYS', stallNumber: '188, 189' },
  { sno: 19, name: 'EDU BOOK DISTRIBUTORS', stallNumber: '137, 138' },
  { sno: 20, name: 'EDUPRO INC', stallNumber: '153, 154' },
  { sno: 21, name: 'ESWAR BOOK CENTRE', stallNumber: '193, 194' },
  { sno: 22, name: 'FROZEN BOOKS', stallNumber: '66, 67' },
  { sno: 23, name: 'HINDU TAMIL THISAI', stallNumber: '22, 23' },
  { sno: 24, name: 'HIGGINBOTHAMS ( P ) LTD', stallNumber: '127, 128' },
  { sno: 25, name: 'JAINCO PUBLISHERS', stallNumber: '162, 163' },
  { sno: 26, name: 'KANNADHASAN PATHIPPAGAM', stallNumber: '33, 34' },
  { sno: 27, name: 'KANNAPPAN PATHIPPAGAM', stallNumber: '197, 198' },
  { sno: 28, name: 'KARPAGAM PUTHAKALAYAM', stallNumber: '103, 104' },
  { sno: 29, name: 'KIZHAKKU PATHIPPAGAM', stallNumber: '199, 200' },
  { sno: 30, name: 'LOTUS MULTIMEDIA', stallNumber: '216, 217' },
  { sno: 31, name: 'MADHUMITHA BOOKS', stallNumber: '29, 30' },
  { sno: 32, name: 'MANIVASAGAR PATHIPPAGAM', stallNumber: '52, 53' },
  { sno: 33, name: 'MANOJ PUBLICATION', stallNumber: '173, 174' },
  { sno: 34, name: 'MARUTHUVA PATHIPPAGAM', stallNumber: '5, 6' },
  { sno: 35, name: 'MATHI NILAYAM', stallNumber: '16, 17' },
  { sno: 36, name: 'MAYILAVAN PATHIPPAGAM', stallNumber: '97, 98' },
  { sno: 37, name: 'MEENAKSHI BOOK SHOP', stallNumber: '160, 161' },
  { sno: 38, name: 'MOTHER LAND BOOK HOUSE', stallNumber: '64, 65' },
  { sno: 39, name: 'NAKKHEERAN', stallNumber: '129, 130' },
  { sno: 40, name: 'NARMADHA PATHIPPAGAM', stallNumber: '112, 113' },
  { sno: 41, name: 'NATIONAL BOOK TRUST', stallNumber: '170, 171' },
  { sno: 42, name: 'OM SAKTHI BOOKS INTERNATIONAL', stallNumber: '18, 19' },
  { sno: 43, name: 'OMNI BOOKS', stallNumber: '117, 118' },
  { sno: 44, name: 'PADAM BOOK COMPANY', stallNumber: '39, 40' },
  { sno: 45, name: 'PARRAGON PUBLISHING PVT LTD', stallNumber: '164, 165' },
  { sno: 46, name: 'PENGUIN RANDOM HOUSE INDIA', stallNumber: '92, 93' },
  { sno: 47, name: 'PRAKASH BOOKS INDIA PVT.LTD', stallNumber: '1, 2' },
  { sno: 48, name: 'PUBLICATIONS DIVISION GOVT.OF.INDIA', stallNumber: '87, 88' },
  { sno: 49, name: 'SAHITYA AKADEMI', stallNumber: '218, 219' },
  { sno: 50, name: 'SAKTHI PUBLISHING HOUSE', stallNumber: '180, 181' },
  { sno: 51, name: 'SAPNA BOOK HOUSE PVT LTD', stallNumber: '26, 27' },
  { sno: 52, name: 'SHANKAR PATHIPPAGAM', stallNumber: '108, 109' },
  { sno: 53, name: 'SHREE BALAJI BOOK SELLER & DISTRIBUTORS', stallNumber: '156, 157' },
  { sno: 54, name: 'SIXTHSENSE PUBLICATIONS', stallNumber: '81, 82' },
  { sno: 55, name: 'SPIDER BOOKS', stallNumber: '145, 146' },
  { sno: 56, name: 'SRI BHARATHI PUBLICATIONS', stallNumber: '43, 44' },
  { sno: 57, name: 'SRI RAMAKRISHNA MATH', stallNumber: '209, 210' },
  { sno: 58, name: 'SRIRAM BOOKS', stallNumber: '79, 80' },
  { sno: 59, name: 'SUCCESS BOOK SELLERS', stallNumber: '178, 179' },
  { sno: 60, name: 'TAMILNADU PAADA NOOL KALAGAM', stallNumber: '147, 148' },
  { sno: 61, name: 'THE PERIYAR SELF RESPECT PROPAGANDA INSTUTION', stallNumber: '158, 159' },
  { sno: 62, name: 'THUMBI TANNARAM NULVELI', stallNumber: '75, 76' },
  { sno: 63, name: 'TIGER BOOKS PVT LTD', stallNumber: '59, 60' },
  { sno: 64, name: 'UNIVERSAL PUBLISHERS', stallNumber: '220, 221' },
  { sno: 65, name: 'UPKAR PRAKASHAN', stallNumber: '47, 48' },
  { sno: 66, name: 'V.O.C. NOOLAGAM', stallNumber: '54, 55' },
  { sno: 67, name: 'VALLALAR BOOK SHOP', stallNumber: '223, 224' },
  { sno: 68, name: 'VALLI PATHIPAGAM', stallNumber: '202, 203' },
  { sno: 69, name: 'WISDOM GLOBAL SERVICE', stallNumber: '184, 185' },
  { sno: 70, name: 'YEGAM PATHIPAGAM', stallNumber: '99, 100' }
];

export default Update;